import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom-v5-compat";
import {
  Loader,
  Details,
  DetailsPicture,
  DetailsHeaderItem,
  ActionBar,
  ActionBarButtons,
  DeleteActionButton,
  EditActionButton,
  DetailsItem,
  PictureGallery,
  apiShape,
  intlShape,
  notifierShape,
  accessRightsShape,
  withApi,
  withIntl,
  withNotifier,
  sortBy,
  withAccessRights,
  extractPictures,
  statusIconClassName,
  statusForCriticality,
  url,
} from "lcm-iot-commons";
import { extractProductCategoryItem } from "lcm-iot-commons/client/lib/utils/productUtils";
import { ProductDocuments } from "lcm-iot-commons/client/lib/components/Products";

type Props = {
  intl: intlShape;
  api: apiShape;
  notifier: notifierShape;
  match: {
    params: {
      id: string;
    };
  };
  accessRights: accessRightsShape;
};

export function ProductDetails({
  intl,
  api,
  notifier,
  match,
  accessRights,
}: Props) {
  const [fetching, setFetching] = useState(true);
  const [productDetails, setProductDetails] = useState(null);
  const [productCategories, setProductCategories] = useState(null);
  const navigate = useNavigate();

  const onConfirmDelete = async () => {
    try {
      await api.delete(`/products/${match.params.id}`);
      notifier.showSuccess(
        intl.formatMessage({ id: "product.actions.delete.notification" }),
      );
      navigate(url("/products"));
    } catch (e) {
      notifier.showError(api.translateError(e));
    }
  };

  const extractProductCategories = (
    allCategories: $TSFixMe,
    categories: $TSFixMe,
  ) => {
    const mappedCategories = allCategories.categories.map(
      (category: $TSFixMe) => extractProductCategoryItem(category),
    );
    const foundDisplayed: $TSFixMe = [];
    categories.categories.map((cat: $TSFixMe) => {
      const mappedCategory = mappedCategories.find(
        (c: $TSFixMe) => c.id === cat.id,
      );
      foundDisplayed.push({
        id: mappedCategory.id,
        displayName: mappedCategory.name,
      });
      return foundDisplayed;
    });
    setProductCategories(sortBy(foundDisplayed, "displayName"));
  };

  const loadDetails = async () => {
    try {
      const product = await api.get(
        `/products/${match.params.id}`,
        { include: "tenant,manufacturer,parent,pictures,status" },
        false,
      );
      setProductDetails({
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ name: any; description: any; p... Remove this comment to see the full error message
        name: product.name,
        description: product.description,
        productCode: product.product_code,
        pictures: product.pictures ? extractPictures(product) : null,
        statusName: product.status.name,
        tenantName: product.tenant.name,
        parentName: product.parent ? product.parent.name : null,
        manufacturerName: product.manufacturer.name,
        riskOfMaintainability: product.risk_of_maintainability,
      });
      const allCategories = await api.getAll(
        "/product/categories",
        { include: "tenant,parent" },
        false,
      );
      const categories = await api.getAll(
        `/products/${match.params.id}/categories`,
        { include: "tenant,parent" },
        false,
      );
      extractProductCategories(allCategories, categories);
    } catch (error) {
      notifier.showError(api.translateError(error));
    } finally {
      setFetching(false);
    }
  };

  React.useEffect(() => {
    loadDetails();
  }, [match.params.id]);

  const categories = productDetails ? (
    <div id="product-categories" className="details-item">
      <div id="categories-title" className="details-item-name">
        {(productCategories as $TSFixMe)?.length > 1 ? (
          <FormattedMessage id="label.categories" />
        ) : (
          <FormattedMessage id="label.category" />
        )}
      </div>
      {(productCategories as $TSFixMe)?.length > 0 ? (
        <ul id="categories-list" className="list">
          {/* @ts-expect-error TS(2531) FIXME: Object is possibly 'null'. */}
          {productCategories.map((item: $TSFixMe) => (
            <div key={item.id}>
              <div
                data-testid="category-item"
                id="category-item"
                className="details-item-value"
              >
                {item.displayName}
              </div>
            </div>
          ))}
        </ul>
      ) : (
        "-"
      )}
    </div>
  ) : null;

  const productDocumentList = (
    <div className="space-after">
      <ProductDocuments
        // @ts-expect-error TS(2322) FIXME: Type '{ id: string; object: { id: number; productI... Remove this comment to see the full error message
        id="product-document-list"
        object={{
          ...match.params,
          id: Number(match.params.id),
          productId: Number(match.params.id),
        }}
        canCreate={accessRights.canUpdate}
        withCreate
        target={`/products/${match.params.id}/documents/create`}
      />
    </div>
  );

  const renderDetails = () =>
    productDetails ? (
      <Details>
        <DetailsPicture>
          <PictureGallery
            pictures={(productDetails as $TSFixMe).pictures}
            placeholder="lcm-iot-icon-product"
          />
        </DetailsPicture>
        <DetailsHeaderItem
          id="product-name"
          translationKey="label.name"
          value={(productDetails as $TSFixMe).name}
        />
        <DetailsItem
          id="product-manufacturer"
          translationKey="label.manufacturer"
          value={(productDetails as $TSFixMe).manufacturerName}
        />
        <DetailsItem
          id="product-tenant"
          translationKey="label.tenant"
          value={(productDetails as $TSFixMe).tenantName}
        />
        <DetailsItem
          id="product-code"
          translationKey="label.product_code"
          value={(productDetails as $TSFixMe).productCode}
        />
        <DetailsItem
          id="product-parent"
          translationKey="label.product_parent"
          value={(productDetails as $TSFixMe).parentName}
        />
        <DetailsItem
          id="product-description"
          translationKey="label.description"
          value={(productDetails as $TSFixMe).description}
        />
        <DetailsItem
          id="product-status"
          translationKey="label.status"
          value={(productDetails as $TSFixMe).statusName}
        />
        <DetailsItem
          id="product-risk-of-maintainability"
          icon={statusIconClassName(
            statusForCriticality(
              (productDetails as $TSFixMe)?.riskOfMaintainability,
            ),
          )}
          translationKey="product.risk_of_maintainability"
          value={intl.formatMessage({
            id: `product.risk_of_maintainability.${(productDetails as $TSFixMe)?.riskOfMaintainability}`,
          })}
        />
        {categories}
        {productDocumentList}
      </Details>
    ) : null;

  return (
    <div className="container">
      <div className="row">
        <div className="col-xs-12">
          <ActionBar>
            <h1 id="details-header">
              <FormattedMessage id="product_details.header" />
            </h1>
            <ActionBarButtons>
              <EditActionButton
                id="edit-product-button"
                disabled={!accessRights.canUpdate || fetching}
                target={`/products/${match.params.id}/edit`}
              />
              <DeleteActionButton
                id="delete-product-button"
                disabled={!accessRights.canDelete || fetching}
                modalTitle={intl.formatMessage({
                  id: "product.actions.delete.modal_title",
                })}
                modalMessage={intl.formatMessage({
                  id: "product.actions.delete.modal_message",
                })}
                onConfirm={onConfirmDelete}
              />
            </ActionBarButtons>
          </ActionBar>
          {fetching ? null : renderDetails()}
          <Loader id="product-details-loader" loading={fetching} />
        </div>
      </div>
    </div>
  );
}

export default withIntl(
  // @ts-expect-error TS(2345) FIXME: Argument of type '"product"' is not assignable to ... Remove this comment to see the full error message
  withNotifier(withApi(withAccessRights(ProductDetails, "product"))),
);
